import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { apiMerchantDefinedFieldsIdFormatGetOptions } from "@/generated/api/@tanstack/react-query.gen";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ContainerInset from "@/components/container-inset";
import FormRequiredMark from "@/components/form-required-mark";
import { PageErrorNotFound } from "@/components/page-error";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider";
import MerchantDefinedFieldsForm from "../../_components/merchant-defined-fields-form";
import { merchantDefinedFieldFormSchema } from "../../_components/merchant-defined-fields-form-schema";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/merchant-defined-fields/$id/edit/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();
  const merchantAccountId = selectedMerchant?.merchAccountId || "";
  const merchantDefinedFieldsDetailQuery = useQuery({
    ...apiMerchantDefinedFieldsIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": merchantAccountId,
      },
      path: {
        id,
      },
    }),
    enabled: !!merchantAccountId,
  });
  const renderContent = () => {
    if (merchantDefinedFieldsDetailQuery.isPending) {
      return (
        <div className="space-y-2">
          <h3 className="border-border mb-4 border-b pb-1 text-lg font-semibold">
            Merchant Defined Fields
          </h3>
          <div className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              Name <FormRequiredMark />
              <Skeleton className="mt-2 h-8 w-full" />
            </div>
            <div>
              <div>
                Type <FormRequiredMark />
              </div>
              <Skeleton className="mt-2 h-8 w-full" />
            </div>
          </div>
        </div>
      );
    }

    if (
      merchantDefinedFieldsDetailQuery.error ||
      !merchantDefinedFieldsDetailQuery.data
    ) {
      return (
        <PageErrorNotFound
          message={
            merchantDefinedFieldsDetailQuery.error?.message ||
            "Merchant Defined Fields not found"
          }
          bottomRender={
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          }
        />
      );
    }
    const dataEdit: z.infer<typeof merchantDefinedFieldFormSchema> =
      merchantDefinedFieldsDetailQuery.data.type === "Radio" ||
      merchantDefinedFieldsDetailQuery.data.type === "Select"
        ? {
            name: merchantDefinedFieldsDetailQuery.data.name ?? "",
            value:
              merchantDefinedFieldsDetailQuery.data.value?.map((item) => ({
                value: item ?? "",
              })) ?? [],
            type: merchantDefinedFieldsDetailQuery.data.type,
          }
        : {
            name: merchantDefinedFieldsDetailQuery.data.name ?? "",
            type: merchantDefinedFieldsDetailQuery.data.type!,
          };

    return (
      <div className="flex min-h-[40vh] flex-col justify-between">
        <MerchantDefinedFieldsForm
          defaultMerchantDefinedField={dataEdit}
          editMDFId={merchantDefinedFieldsDetailQuery.data.id}
        />
      </div>
    );
  };

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Merchant Defined Field",
          href: `/${merchantAccountId}/merchant-defined-fields`,
        },
        {
          title: "Edit",
          href: "#",
        },
      ]}
    >
      <PageLayoutRoot>
        <PageLayoutHeader
          title="Edit Merchant Defined Fields"
          description={
            <span>
              To configure this field, first choose a name, followed by the type
              of field you wish to use. 'Text' is the default and permit any
              text entry. 'Checkbox' is either on or off corresponding to a Yes
              or No value. 'Radio' and 'Select' provide discrete option from a
              list of values you choose to provide. You may also{" "}
              <Link
                to="/$merchantId/merchant-defined-fields/$id/preview"
                className="text-primary hover:underline"
                params={{
                  id,
                  merchantId: merchantAccountId,
                }}
              >
                preview this field here.
              </Link>
            </span>
          }
        />

        <PageLayoutContent>{renderContent()}</PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
